import { useState, useEffect, useMemo } from 'react';
import { Stack, Title, Group, Button, Text, Box, Card, Skeleton, Anchor, Alert } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft } from '@tabler/icons-react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSubAccounts } from '../../hooks/useSubAccounts';
import { useCreateSubAccount, useUpdateSubAccount } from '../../hooks/useSubAccountMutations';
import { AccountUtils } from '../../AccountUtils';
import { SubAccountFormFields } from './SubAccountForm';
import { SubAccountCaptionGrantSwitch } from '@/features/captions/components/SubAccountCaptionGrantSwitch';
import {
  subAccountCreateSchema,
  subAccountEditSchema,
  type SubAccountCreateFormValues,
  type SubAccountEditFormValues,
} from '../../schemas/subAccountSchemas';
import type { SubaccountCreateReq, SubaccountUpdateReq } from '@/lib/types/Subaccount';
import type { CustomercfgQuotaFeaturesApp, CustomerAllData } from '@/lib/types/Customer';

const DEFAULT_APP_FEATURES: CustomercfgQuotaFeaturesApp = {
  appCfgLimit: null,
  allowWeb: false,
  allowAndroid: false,
  allowIos: false,
  allowRoku: false,
  allowAppletv: false,
  allowFiretv: false,
  allowAndroidtv: false,
};

const DEFAULT_CREATE_VALUES: SubAccountCreateFormValues = {
  label: '',
  storageLimitGB: null,
  transferLimitGB: null,
  appCfgLimit: 0,
  allowDvr: null,
  videostreamLimit: null,
  allowWeb: false,
  allowAndroid: false,
  allowIos: false,
  allowRoku: false,
  allowAppletv: false,
  allowFiretv: false,
  allowAndroidtv: false,
};

/**
 * Escape hatch for validation failures on fields without a rendered input
 * (schema fields seeded from stored data): without this, submit no-ops with
 * no feedback at all — the failure stays invisible forever.
 */
function handleInvalidSubmit(errors: FieldErrors) {
  notifications.show({
    title: 'Cannot save',
    message: `Validation failed: ${Object.keys(errors).join(', ')}`,
    color: 'red',
  });
}

function buildAppFeatures(values: SubAccountCreateFormValues | SubAccountEditFormValues): CustomercfgQuotaFeaturesApp {
  return {
    appCfgLimit: values.appCfgLimit ?? 0,
    allowWeb: values.allowWeb,
    allowAndroid: values.allowAndroid,
    allowIos: values.allowIos,
    allowRoku: values.allowRoku,
    allowAppletv: values.allowAppletv,
    allowFiretv: values.allowFiretv,
    allowAndroidtv: values.allowAndroidtv,
  };
}

export function SubAccountPage() {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const rawId = (params as Record<string, string | undefined>).id;
  const isCreate = !rawId || rawId === 'new';
  const subCId = isCreate ? null : Number(rawId);

  const { subAccounts, masterAccount, usageTotals, isLoading } = useSubAccounts();
  const createMutation = useCreateSubAccount();
  const updateMutation = useUpdateSubAccount();

  // Find the sub-account being edited
  const subAccount = useMemo(() => {
    if (isCreate || !subCId) return null;
    return subAccounts.find(s => s.account.cfg.cId === subCId) ?? null;
  }, [subAccounts, subCId, isCreate]);

  const masterApp = masterAccount?.cfg.quota.features.app ?? DEFAULT_APP_FEATURES;

  // Create form
  const createForm = useForm<SubAccountCreateFormValues>({
    resolver: zodResolver(subAccountCreateSchema),
    defaultValues: DEFAULT_CREATE_VALUES,
  });

  // Edit form
  const editForm = useForm<SubAccountEditFormValues>({
    resolver: zodResolver(subAccountEditSchema),
    defaultValues: {
      ...DEFAULT_CREATE_VALUES,
      isActive: true,
    },
  });

  // Populate edit form when data loads
  useEffect(() => {
    if (subAccount && !isCreate) {
      editForm.reset(AccountUtils.toSubAccountEditFormValues(subAccount.account));
    }
  }, [subAccount, isCreate, editForm]);

  // App limit validation state
  const [appLimitError, setAppLimitError] = useState<string | undefined>();

  const watchedAppLimit = isCreate
    ? createForm.watch('appCfgLimit')
    : editForm.watch('appCfgLimit');

  // Reusable app license info (matches legacy validateAppCfgLimit logic)
  const licenseInfo = useMemo(() => {
    if (!masterAccount) return null;
    return AccountUtils.getAppLicenseInfo(masterAccount, subAccounts, usageTotals, subCId);
  }, [masterAccount, subAccounts, usageTotals, subCId]);

  // Stored limit for the sub-account being edited (null in create mode) —
  // unchanged values are exempt from validation so pre-enforcement accounts
  // can still save other properties.
  const persistedAppCfgLimit = subAccount
    ? subAccount.account.cfg.quota.features.app.appCfgLimit ?? 0
    : null;

  // Validate app limit against master account
  useEffect(() => {
    if (!licenseInfo) return;
    setAppLimitError(
      AccountUtils.validateAppLimitChange(watchedAppLimit ?? 0, persistedAppCfgLimit, licenseInfo)
    );
  }, [watchedAppLimit, licenseInfo, persistedAppCfgLimit]);

  // Helper text for resource limit fields
  const storageHelperText = subAccount
    ? `Currently using ${AccountUtils.formatBytesAsGB(subAccount.storageBytesUsed)}`
    : undefined;
  const transferHelperText = subAccount
    ? `Currently using ${AccountUtils.formatBytesAsGB(subAccount.transferBytesUsed)}`
    : undefined;

  // A grandfathered sub-account can hold more apps than its stored limit.
  // Within entitlement, raising the limit up to in-use is license-free
  // (validateAppLimitChange rule 3) — without this note the remaining count
  // reads "0 available" while that raise is in fact allowed.
  let uncoveredAppsNote = '';
  if (
    licenseInfo &&
    persistedAppCfgLimit !== null &&
    licenseInfo.subAccountAppsInUse > persistedAppCfgLimit &&
    licenseInfo.totalAppsInUse <= licenseInfo.masterAppLimit
  ) {
    const inUse = licenseInfo.subAccountAppsInUse;
    uncoveredAppsNote =
      `. ${inUse} app${inUse === 1 ? '' : 's'} in use ${inUse === 1 ? 'is' : 'are'} ` +
      `not covered by this limit yet; you can raise the limit up to ${inUse}.`;
  }

  const appLimitHelperText = licenseInfo
    ? licenseInfo.masterAppLimit === Number.MAX_SAFE_INTEGER
      ? 'Remaining app licenses available: Unlimited'
      : `Remaining app licenses available for assignment: ${Math.max(0, licenseInfo.appsAvailableForAssignment)}${uncoveredAppsNote}`
    : undefined;

  async function handleCreateSubmit(values: SubAccountCreateFormValues) {
    if (appLimitError) return;

    const req: SubaccountCreateReq = {
      label: values.label,
      allowDvr: values.allowDvr,
      storageBytesLimit: values.storageLimitGB !== null ? AccountUtils.gbToBytes(values.storageLimitGB) : null,
      transferBytesLimit: values.transferLimitGB !== null ? AccountUtils.gbToBytes(values.transferLimitGB) : null,
      videostreamLimit: values.videostreamLimit,
      app: buildAppFeatures(values),
    };

    try {
      await createMutation.mutateAsync(req);
      notifications.show({ title: 'Created', message: 'Sub-account created.', color: 'green' });
      navigate({ to: '/account/sub-accounts' });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to create sub-account.',
        color: 'red',
      });
    }
  }

  async function handleEditSubmit(values: SubAccountEditFormValues) {
    if (!subCId || appLimitError) return;

    const req: SubaccountUpdateReq = {
      customerStateType: values.isActive ? 'active' : 'inactive',
      label: values.label,
      allowDvr: values.allowDvr,
      storageBytesLimit: values.storageLimitGB !== null ? AccountUtils.gbToBytes(values.storageLimitGB) : null,
      transferBytesLimit: values.transferLimitGB !== null ? AccountUtils.gbToBytes(values.transferLimitGB) : null,
      videostreamLimit: values.videostreamLimit,
      app: buildAppFeatures(values),
    };

    try {
      await updateMutation.mutateAsync({ subCId, req });
      notifications.show({ title: 'Saved', message: 'Sub-account updated.', color: 'green' });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to update sub-account.',
        color: 'red',
      });
    }
  }

  if (!isCreate && isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={40} width={200} />
        <Skeleton height={200} />
        <Skeleton height={300} />
      </Stack>
    );
  }

  if (!isCreate && !subAccount) {
    return (
      <Stack gap="lg">
        <Anchor component="button" onClick={() => navigate({ to: '/account/sub-accounts' })}>
          <Group gap={4}><IconArrowLeft size={16} /> Back to Sub-Accounts</Group>
        </Anchor>
        <Alert color="red">Sub-account not found.</Alert>
      </Stack>
    );
  }

  const isPending = isCreate ? createMutation.isPending : updateMutation.isPending;
  const isDirty = isCreate ? createForm.formState.isDirty : editForm.formState.isDirty;
  const pageTitle = isCreate ? 'Add Sub-Account' : subAccount!.account.cfg.label;

  return (
    <Stack gap="lg">
      <Anchor component="button" onClick={() => navigate({ to: '/account/sub-accounts' })} size="sm">
        <Group gap={4}><IconArrowLeft size={16} /> Back to Sub-Accounts</Group>
      </Anchor>

      <Title order={2}>{pageTitle}</Title>

      <Box
        pos="sticky"
        top="calc(var(--app-shell-header-height, 60px) + var(--mantine-spacing-md))"
        bg="white"
        py="sm"
        style={{ zIndex: 10, borderBottom: '1px solid var(--mantine-color-gray-2)' }}
      >
        <Group justify="flex-end">
          <Button
            type="submit"
            form="sub-account-form"
            loading={isPending}
            disabled={!!appLimitError || (!isCreate && !isDirty)}
          >
            {isCreate ? 'Create Sub-Account' : 'Save Changes'}
          </Button>
        </Group>
      </Box>

      <form
        id="sub-account-form"
        onSubmit={isCreate
          ? createForm.handleSubmit(handleCreateSubmit, handleInvalidSubmit)
          : editForm.handleSubmit(handleEditSubmit, handleInvalidSubmit)
        }
      >
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          {isCreate ? (
            <SubAccountFormFields
              control={createForm.control}
              isCreate
              masterApp={masterApp}
              appLimitHelperText={appLimitHelperText}
              appLimitError={appLimitError}
            />
          ) : (
            <SubAccountFormFields
              control={editForm.control}
              isCreate={false}
              masterApp={masterApp}
              storageHelperText={storageHelperText}
              transferHelperText={transferHelperText}
              appLimitHelperText={appLimitHelperText}
              appLimitError={appLimitError}
              instantSaveSection={
                subCId != null && subAccount ? (
                  <SubAccountCaptionGrantSwitch subCId={subCId} subAccount={subAccount.account} />
                ) : undefined
              }
            />
          )}
        </Card>
      </form>
    </Stack>
  );
}
